import type {
  PendingSketchImage,
  ProjectDetailsCharacter,
  ProjectDetailsLocation,
  ProjectDetailsProject,
  ProjectDetailsStoryboardShot
} from "@/types/project";
import { toastWarning } from "@/utils/toast";
import { dataUrlToImageFile, moveImageIdsToFront, prependImageIdIfMissing } from "@/utils/utils";

function escapeRegExp(s: string): string {
  return s.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`);
}

/** Next id like `character-4` / `location-2` from existing entities. */
export function nextHyphenNumberId(items: ReadonlyArray<{ readonly id: string }>, base: "character" | "location"): string {
  const re = new RegExp(`^${escapeRegExp(base)}-(\\d+)$`);
  let max = 0;
  for (const item of items) {
    const m = re.exec(item.id);
    if (m) {
      max = Math.max(max, Number(m[1]));
    }
  }

  return `${base}-${max + 1}`;
}

export function displayNameForHyphenId(id: string, base: "character" | "location"): string {
  const re = new RegExp(`^${escapeRegExp(base)}-(\\d+)$`);
  const m = re.exec(id);
  const n = m ? m[1] : id;
  return base === "character" ? `Character ${n}` : `Location ${n}`;
}

/** Next unique `shot-id` within a scene (for duplicates), scanning all shots. `id` is a
 * fresh opaque UUID - it's an internal handle only, never derived from or shown alongside
 * `scene-id`/`shot-id`. */
export function nextStoryboardShotIdInScene(
  sceneId: string,
  shots: readonly ProjectDetailsStoryboardShot[]
): { readonly id: string; readonly shotIdPart: string } {
  let maxN = 0;
  const shotRe = /^shot-(\d+)$/;

  for (const s of shots) {
    if (s["scene-id"] !== sceneId) {
      continue;
    }

    const m = shotRe.exec(s["shot-id"]);
    if (m) {
      maxN = Math.max(maxN, Number(m[1]));
    }
  }

  const n = maxN + 1;
  const shotIdPart = `shot-${n}`;

  return { id: crypto.randomUUID(), shotIdPart };
}

/** Next scene/shot-id/name for a newly added shot. `id` is a fresh opaque UUID, unrelated
 * to `scene-id`/`shot-id` - see `nextStoryboardShotIdInScene`'s comment above. */
export function nextStoryboardShotKeys(
  shots: readonly ProjectDetailsStoryboardShot[],
  defaultSceneId: string
): { readonly id: string; readonly sceneId: string; readonly shotIdPart: string; readonly shotName: string } {
  const sceneId = shots.length > 0 ? (shots.at(-1)?.["scene-id"] ?? defaultSceneId) : defaultSceneId;

  let maxN = 0;
  const shotRe = /^shot-(\d+)$/;

  for (const s of shots) {
    if (s["scene-id"] !== sceneId) {
      continue;
    }

    const m = shotRe.exec(s["shot-id"]);
    if (m) {
      maxN = Math.max(maxN, Number(m[1]));
    }
  }

  const n = maxN + 1;
  const shotIdPart = `shot-${n}`;
  return {
    sceneId,
    shotIdPart,
    shotName: shotIdPart,
    id: crypto.randomUUID()
  };
}

export function createDefaultCharacter(id: string, name: string): ProjectDetailsCharacter {
  return {
    id: id,
    name: name,
    description: "",
    appearance: "",
    images: [],
    "approved-images": [],
    uploaded_images: []
  };
}

export function createDefaultLocation(id: string, name: string): ProjectDetailsLocation {
  return {
    id: id,
    name: name,
    description: "",
    appearance: "",
    images: [],
    "approved-images": [],
    uploaded_images: []
  };
}

export function patchCharacterApprovedImage(character: ProjectDetailsCharacter, imageId: string): ProjectDetailsCharacter {
  return {
    ...character,
    images: prependImageIdIfMissing(character.images, imageId),
    "approved-images": [imageId]
  };
}

export function patchLocationApprovedImage(location: ProjectDetailsLocation, imageId: string): ProjectDetailsLocation {
  return {
    ...location,
    images: prependImageIdIfMissing(location.images, imageId),
    "approved-images": [imageId]
  };
}

export type FavoriteMediaKind = "image" | "video";

/** True if `mediaId` is in the project's shared favorites list for its kind. */
export function isProjectMediaFavorited(
  project: ProjectDetailsProject | null | undefined,
  mediaId: string | null | undefined,
  kind: FavoriteMediaKind
): boolean {
  if (!project || mediaId === null || mediaId === undefined || mediaId === "") {
    return false;
  }

  const key = kind === "video" ? "favorite-video-ids" : "favorite-images";
  return (project[key] ?? []).includes(mediaId);
}

/** Toggle `mediaId` in/out of the project's shared favorites list for its kind. */
export function patchProjectFavoriteMedia(project: ProjectDetailsProject, mediaId: string, kind: FavoriteMediaKind): ProjectDetailsProject {
  const key = kind === "video" ? "favorite-video-ids" : "favorite-images";
  const current = project[key] ?? [];
  const next = current.includes(mediaId) ? current.filter((id) => id !== mediaId) : [...current, mediaId];
  return { ...project, [key]: next };
}

/** Double-click timeline: promote `approved-images[0]` to `images[0]`. */
export function patchCharacterPromoteApprovedToImagesFront(character: ProjectDetailsCharacter): ProjectDetailsCharacter | null {
  const imageId = character["approved-images"]?.[0];
  if (typeof imageId !== "string" || imageId === "") {
    return null;
  }

  const nextImages = moveImageIdsToFront(character.images, [imageId]);
  const prevImages = character.images ?? [];
  if (nextImages.length === prevImages.length && nextImages.every((id, index) => id === prevImages[index])) {
    return null;
  }

  return { ...character, images: nextImages };
}

/** Double-click timeline: promote `approved-images[0]` to `images[0]`. */
export function patchLocationPromoteApprovedToImagesFront(location: ProjectDetailsLocation): ProjectDetailsLocation | null {
  const imageId = location["approved-images"]?.[0];
  if (typeof imageId !== "string" || imageId === "") {
    return null;
  }

  const nextImages = moveImageIdsToFront(location.images, [imageId]);
  const prevImages = location.images ?? [];
  if (nextImages.length === prevImages.length && nextImages.every((id, index) => id === prevImages[index])) {
    return null;
  }

  return { ...location, images: nextImages };
}

export function createDefaultStoryboardShot(keys: {
  readonly id: string;
  readonly sceneId: string;
  readonly shotIdPart: string;
  readonly shotName: string;
}): ProjectDetailsStoryboardShot {
  return {
    id: keys.id,
    "scene-id": keys.sceneId,
    "shot-id": keys.shotIdPart,
    "shot-name": keys.shotName,
    "shot-description": "",
    "ref-image-prompt": "",
    "video-gen-prompt": "",
    "assumed-video-duration": 8,
    characters: [],
    locations: [],
    dialogues: "",
    "selected-location-images": [],
    "selected-character-images": [],
    images: [],
    uploaded_images: [],
    "video-uploaded-images": [],
    "approved-timeline-images": [],
    "approved-video-id": "",
    visuals: [],
    "sound-effects": []
  };
}

/** Ordered reference image ids from project JSON; missing field → empty array. */
export function parseUploadedImages(raw: readonly string[] | undefined): string[] {
  return (raw ?? []).filter((id): id is string => typeof id === "string" && id.trim() !== "");
}

/** Flattens entities to one option per image id (e.g. character/location approved images for frame UI). */
export function flatMapEntityImageOptions<T extends { name: string }>(
  entities: readonly T[],
  getImageIds: (entity: T) => readonly string[] | undefined
): Array<{ id: string; name: string; imageId: string; entity: T }> {
  return entities.flatMap((entity) => {
    const ids = getImageIds(entity) ?? [];
    const name = (i: number) => entity.name;
    return ids.map((imageId, i) => ({ id: imageId, name: name(i), imageId, entity }));
  });
}

export interface EntityImageOption<T extends { id: string }> {
  id: string;
  name: string;
  imageId: string;
  entity: T;
}

/**
 * Frame panel selection: prefer persisted `selected-*-images` ids; when empty, tick all approved
 * images for entities listed on the shot (`characters` / `locations` entity ids).
 */
export function selectedImageOptionsForShot<T extends { id: string }>(
  options: ReadonlyArray<EntityImageOption<T>>,
  selectedImageIds: readonly string[],
  shotEntityIds: readonly string[]
): Array<EntityImageOption<T>> {
  if (selectedImageIds.length > 0) {
    return options.filter((opt) => selectedImageIds.includes(opt.imageId));
  }

  if (shotEntityIds.length === 0) {
    return [];
  }

  const entityIdSet = new Set(shotEntityIds);
  return options.filter((opt) => entityIdSet.has(opt.entity.id));
}

/**
 * Blocks image generation when reference count exceeds the selected model’s limit.
 * @returns `true` if the run may proceed, `false` after showing a warning toast.
 */
export function assertReferenceImagesWithinModelLimit(referenceCount: number, maxAllowed: number): boolean {
  if (referenceCount > maxAllowed) {
    toastWarning(`Remove extra reference images before generating. This model allows at most ${maxAllowed} (you have ${referenceCount}).`);
    return false;
  }

  return true;
}

/** Character + location + extra reference image ids in order, first occurrence wins (for create-image `reference_image_ids`). */
export function mergeFrameReferenceImageIds(
  characterImageIds: readonly string[],
  locationImageIds: readonly string[],
  extraReferenceImageIds: readonly string[]
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of [...characterImageIds, ...locationImageIds, ...extraReferenceImageIds]) {
    if (typeof id !== "string" || id.length === 0 || seen.has(id)) {
      continue;
    }

    seen.add(id);
    out.push(id);
  }

  return out;
}

/**
 * Uploads pending (not-yet-saved) sketches in parallel, tagged `source: "sketch"` and `parent_image_ids: [sourceImageId]`.
 * Calls `onUploaded(localId)` for each individual success so the caller can drop it from pending state right away —
 * a failed upload is skipped (logged, not thrown) so the rest still proceed and the failed one stays pending for the user to retry.
 */
export async function uploadPendingSketchesForRun(
  pendingSketches: readonly PendingSketchImage[],
  entityName: string,
  uploadImage: (file: File, source: string, parentImageIds?: readonly string[]) => Promise<{ readonly imageId: string }>,
  onUploaded: (localId: string) => void
): Promise<string[]> {
  const baseTimestamp = Date.now();
  const results = await Promise.allSettled(
    pendingSketches.map(async (sketch, index) => {
      const displayName = `${entityName} — Sketch — ${baseTimestamp + index}`;
      const file = await dataUrlToImageFile(sketch.dataUrl, displayName);
      const { imageId } = await uploadImage(file, "sketch", sketch.sourceImageId ? [sketch.sourceImageId] : undefined);
      return { localId: sketch.localId, imageId };
    })
  );

  const uploadedImageIds: string[] = [];
  for (const result of results) {
    if (result.status === "fulfilled") {
      uploadedImageIds.push(result.value.imageId);
      onUploaded(result.value.localId);
    } else {
      console.warn("Sketch upload failed:", result.reason);
    }
  }

  return uploadedImageIds;
}

/** Primary image first, then extra reference ids (deduped) for create-image `reference_image_ids`. */
export function mergePrimaryWithReferenceImageIds(primaryImageId: string, additionalReferenceImageIds: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of [primaryImageId, ...additionalReferenceImageIds]) {
    if (typeof id !== "string" || id.length === 0 || seen.has(id)) {
      continue;
    }

    seen.add(id);
    out.push(id);
  }

  return out;
}
